import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useLanguage } from '../../context/LanguageContext';
import { Search, MapPin, ChevronUp } from 'lucide-react';

const GMap = ({ locations }) => {
  const { language } = useLanguage();
  const [map, setMap] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);
  const locationListRef = useRef(null);

  // Load Google Maps with current language
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    language: language?.languagecode || 'en',
  });

  // Initialize locations and select first location
  useEffect(() => {
    if (locations && locations.length > 0) {
      setFilteredLocations(locations);
      // Select first location by default (Option A)
      const firstLocation = locations[0];
      setSelectedLocation(firstLocation);
      setMapCenter({
        lat: firstLocation.latitude,
        lng: firstLocation.longitude,
      });
    }
  }, [locations]);

  // Filter locations by search term (name or address)
  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setFilteredLocations(locations);
      return;
    }

    const filtered = locations.filter((loc) => {
      const nameMatch = loc.name.toLowerCase().includes(value.toLowerCase());
      const addressMatch = loc.address.toLowerCase().includes(value.toLowerCase());
      return nameMatch || addressMatch;
    });

    setFilteredLocations(filtered);
  }, [locations]);

  // Handle location selection from list
  const handleLocationSelect = useCallback((location) => {
    setSelectedLocation(location);
    setMapCenter({
      lat: location.latitude,
      lng: location.longitude,
    });
    // On mobile, collapse drawer after selection
    if (window.innerWidth < 768) {
      setIsDrawerExpanded(false);
    }
  }, []);

  // Handle marker click
  const handleMarkerClick = useCallback((location) => {
    setSelectedLocation(location);
    setMapCenter({
      lat: location.latitude,
      lng: location.longitude,
    });

    // Scroll to location in list
    if (locationListRef.current) {
      const locationElement = locationListRef.current.querySelector(
        `[data-location-id="${location.name}"]`
      );
      if (locationElement) {
        locationElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (!isLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-theme-secondary">Loading map...</div>
      </div>
    );
  }

  return (
    <>
      {/* DESKTOP LAYOUT - Hidden on mobile */}
      <div className="hidden md:flex h-full">
        {/* Left Panel - Location List (33%) */}
        <div className="w-1/3 border-r border-theme-border flex flex-col h-full">
          {/* Search Input */}
          <div className="p-4 border-b border-theme-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-sub-title" />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-theme-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-theme-primary"
                placeholder="Search by store name or address"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Location List */}
          <div ref={locationListRef} className="flex-1 overflow-y-auto">
            {filteredLocations.length > 0 ? (
              filteredLocations.map((loc, index) => {
                const isSelected = selectedLocation?.name === loc.name;
                return (
                  <div
                    key={index}
                    data-location-id={loc.name}
                    className={`relative p-4 border-b border-theme-border cursor-pointer transition-colors ${
                      isSelected ? 'bg-theme-primary/10' : 'hover:bg-theme-secondary-foreground'
                    }`}
                    onClick={() => handleLocationSelect(loc)}
                  >
                    <div className="flex items-start">
                      <MapPin className={`w-5 h-5 mt-1 flex-shrink-0 ${isSelected ? 'text-theme-primary' : 'text-theme-sub-title'}`} />
                      <div className="ml-3 flex-1">
                        <p className={`font-bold text-sm ${isSelected ? 'text-theme-primary' : 'text-theme-secondary'}`}>
                          {loc.name}
                        </p>
                        <p className="text-xs text-theme-sub-title mt-1">{loc.address}</p>
                      </div>
                      <ChevronUp className={`w-4 h-4 mt-1 -rotate-90 ${isSelected ? 'text-theme-primary' : 'text-theme-sub-title'}`} />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center justify-center h-32">
                <p className="text-sm text-theme-sub-title">No locations found</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Map (67%) */}
        <div className="flex-1 relative">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={15}
            onUnmount={onUnmount}
          >
            {filteredLocations.map((loc, index) => (
              <Marker
                key={index}
                position={{ lat: loc.latitude, lng: loc.longitude }}
                onClick={() => handleMarkerClick(loc)}
              />
            ))}

            {selectedLocation && mapCenter && (
              <InfoWindow
                position={mapCenter}
                onCloseClick={() => setSelectedLocation(null)}
              >
                <div className="p-2">
                  <p className="font-bold text-sm text-theme-secondary">{selectedLocation.name}</p>
                  <p className="text-xs text-theme-sub-title mt-1">{selectedLocation.address}</p>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>
      </div>

      {/* MOBILE LAYOUT - Bottom Drawer (Option C) */}
      <div className="md:hidden flex flex-col h-full relative">
        {/* Full-Screen Map */}
        <div className="absolute inset-0">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={15}
            onUnmount={onUnmount}
          >
            {filteredLocations.map((loc, index) => (
              <Marker
                key={index}
                position={{ lat: loc.latitude, lng: loc.longitude }}
                onClick={() => handleMarkerClick(loc)}
              />
            ))}

            {selectedLocation && mapCenter && (
              <InfoWindow
                position={mapCenter}
                onCloseClick={() => setSelectedLocation(null)}
              >
                <div className="p-2">
                  <p className="font-bold text-sm text-theme-secondary">{selectedLocation.name}</p>
                  <p className="text-xs text-theme-sub-title mt-1">{selectedLocation.address}</p>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>

        {/* Floating Search Bar */}
        <div className="absolute top-4 left-4 right-4 z-10">
          <div className="bg-white rounded-lg shadow-lg">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-sub-title" />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-3 rounded-lg text-sm focus:outline-none"
                placeholder="Search by store name or address"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Bottom Drawer */}
        <div
          className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl transition-all duration-300 ease-out z-20 ${
            isDrawerExpanded ? 'h-[70vh]' : 'h-[180px]'
          }`}
        >
          {/* Drawer Handle */}
          <div
            className="flex justify-center items-center py-3 cursor-pointer"
            onClick={() => setIsDrawerExpanded(!isDrawerExpanded)}
          >
            <div className="w-12 h-1 bg-theme-border rounded-full" />
          </div>

          {/* Drawer Header */}
          <div className="px-4 pb-3 border-b border-theme-border">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-theme-secondary">
                {filteredLocations.length} Location{filteredLocations.length !== 1 ? 's' : ''}
              </h3>
              <button
                onClick={() => setIsDrawerExpanded(!isDrawerExpanded)}
                className="text-xs text-theme-primary font-semibold"
              >
                {isDrawerExpanded ? 'Minimize' : 'View All'}
              </button>
            </div>
          </div>

          {/* Location List */}
          <div
            ref={locationListRef}
            className={`overflow-y-auto ${isDrawerExpanded ? 'h-[calc(70vh-80px)]' : 'h-[100px]'}`}
          >
            {filteredLocations.length > 0 ? (
              filteredLocations.map((loc, index) => {
                const isSelected = selectedLocation?.name === loc.name;
                return (
                  <div
                    key={index}
                    data-location-id={loc.name}
                    className={`relative p-4 border-b border-theme-border cursor-pointer transition-colors ${
                      isSelected ? 'bg-theme-primary/10' : 'active:bg-theme-secondary-foreground'
                    }`}
                    onClick={() => handleLocationSelect(loc)}
                  >
                    <div className="flex items-start">
                      <MapPin className={`w-5 h-5 mt-1 flex-shrink-0 ${isSelected ? 'text-theme-primary' : 'text-theme-sub-title'}`} />
                      <div className="ml-3 flex-1">
                        <p className={`font-bold text-sm ${isSelected ? 'text-theme-primary' : 'text-theme-secondary'}`}>
                          {loc.name}
                        </p>
                        <p className="text-xs text-theme-sub-title mt-1">{loc.address}</p>
                      </div>
                      <ChevronUp className={`w-4 h-4 mt-1 -rotate-90 ${isSelected ? 'text-theme-primary' : 'text-theme-sub-title'}`} />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center justify-center h-32">
                <p className="text-sm text-theme-sub-title">No locations found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GMap;
